import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Input, Label, RequiredMark } from '@/components/ui';
import { ArtistSearchSelect } from '@/features/artist/components/ArtistSearchSelect';
import { ProductSelector } from '@/features/catalog/components/ProductSelector';
import type { ReleaseScheduleCreateRequest } from '@/lib/api-admin/types';
import {
  emptyReleaseScheduleCreateValues,
  releaseScheduleCreateSchema,
  type ReleaseScheduleCreateValues,
} from '../schemas/settlementFormSchema';

const toIso = (dateTimeLocal: string): string => `${dateTimeLocal}:00.000Z`;

const toCreateBody = (values: ReleaseScheduleCreateValues): ReleaseScheduleCreateRequest => {
  const body: ReleaseScheduleCreateRequest = {
    vendorId: values.vendorId,
    title: values.title,
    releaseAt: toIso(values.releaseAt),
  };
  if (values.artistId && values.artistId.length > 0) body.artistId = values.artistId;
  if (values.productId && values.productId.length > 0) body.productId = values.productId;
  if (values.description && values.description.length > 0)
    body.description = values.description;
  return body;
};

export interface ReleaseCreateModalProps {
  isOpen: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onConfirm: (body: ReleaseScheduleCreateRequest) => void;
  onCancel: () => void;
}

export const ReleaseCreateModal = ({
  isOpen,
  isSubmitting,
  errorMessage,
  onConfirm,
  onCancel,
}: ReleaseCreateModalProps) => {
  const { t } = useTranslation('settlement');
  const form = useForm<ReleaseScheduleCreateValues>({
    resolver: zodResolver(releaseScheduleCreateSchema),
    defaultValues: emptyReleaseScheduleCreateValues(),
    mode: 'onBlur',
  });

  const fieldError = (name: keyof ReleaseScheduleCreateValues): string | undefined => {
    const e = form.formState.errors[name];
    if (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string') {
      return e.message;
    }
    return undefined;
  };

  const submit = (values: ReleaseScheduleCreateValues) => {
    onConfirm(toCreateBody(values));
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o && !isSubmitting) onCancel(); }}>
      <DialogContent data-testid="release-create-modal" className="max-w-lg">
        <DialogTitle>{t('settlement.release.create.title')}</DialogTitle>
        <DialogDescription>{t('settlement.release.create.subject')}</DialogDescription>

        <form
          onSubmit={form.handleSubmit(submit)}
          className="grid grid-cols-1 gap-3 md:grid-cols-2"
          noValidate
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor="release-vendorId">
              {t('settlement.release.fields.vendorId')}
              <RequiredMark />
            </Label>
            <Input
              id="release-vendorId"
              {...form.register('vendorId')}
              maxLength={32}
              aria-invalid={Boolean(fieldError('vendorId'))}
              style={{ height: 'var(--density-compact-form-h)' }}
            />
            {fieldError('vendorId') ? (
              <span
                role="alert"
                className="text-xs"
                style={{ color: 'var(--signal-danger)' }}
              >
                {t(fieldError('vendorId')!, { defaultValue: fieldError('vendorId')! })}
              </span>
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="release-artistId">{t('settlement.release.fields.artistId')}</Label>
            <Controller
              control={form.control}
              name="artistId"
              render={({ field }) => (
                <ArtistSearchSelect
                  inputId="release-artistId"
                  value={field.value && field.value.length > 0 ? field.value : null}
                  onChange={(v) => field.onChange(v ?? '')}
                  ariaLabel={t('settlement.release.fields.artistId')}
                />
              )}
            />
          </div>

          <div className="md:col-span-2 flex flex-col gap-1">
            <Label htmlFor="release-title">
              {t('settlement.release.fields.title')}
              <RequiredMark />
            </Label>
            <Input
              id="release-title"
              {...form.register('title')}
              maxLength={200}
              aria-invalid={Boolean(fieldError('title'))}
              style={{ height: 'var(--density-compact-form-h)' }}
            />
            {fieldError('title') ? (
              <span
                role="alert"
                className="text-xs"
                style={{ color: 'var(--signal-danger)' }}
              >
                {t(fieldError('title')!, { defaultValue: fieldError('title')! })}
              </span>
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="release-releaseAt">
              {t('settlement.release.fields.releaseAt')}
              <RequiredMark />
            </Label>
            <Input
              id="release-releaseAt"
              type="datetime-local"
              {...form.register('releaseAt')}
              aria-invalid={Boolean(fieldError('releaseAt'))}
              style={{ height: 'var(--density-compact-form-h)' }}
            />
            {fieldError('releaseAt') ? (
              <span
                role="alert"
                className="text-xs"
                style={{ color: 'var(--signal-danger)' }}
              >
                {t(fieldError('releaseAt')!, { defaultValue: fieldError('releaseAt')! })}
              </span>
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="release-productId">{t('settlement.release.fields.productId')}</Label>
            <Controller
              control={form.control}
              name="productId"
              render={({ field }) => (
                <ProductSelector
                  value={field.value && field.value.length > 0 ? field.value : null}
                  onChange={(v) => field.onChange(v ?? '')}
                />
              )}
            />
          </div>

          <div className="md:col-span-2 flex flex-col gap-1">
            <Label htmlFor="release-description">
              {t('settlement.release.fields.description')}
            </Label>
            <textarea
              id="release-description"
              {...form.register('description')}
              maxLength={4000}
              rows={4}
              className="w-full rounded-md border px-3 py-2 text-sm bg-[color:var(--surface-0)] text-[color:var(--content-strong)] focus-visible:outline-none focus-visible:ring-2"
              style={{ borderColor: 'var(--content-muted)' }}
            />
          </div>

          {errorMessage ? (
            <p
              role="alert"
              className="md:col-span-2 rounded-md p-2 text-sm"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
              }}
            >
              {errorMessage}
            </p>
          ) : null}

          <div className="md:col-span-2 mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('settlement.actions.cancel')}
            </Button>
            <Button type="submit" variant="cta" size="md" disabled={isSubmitting}>
              {isSubmitting
                ? t('settlement.release.create.submitting')
                : t('settlement.actions.saveSchedule')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
